import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import BeefreeSDK from "@beefree.io/sdk";
import type { BeePluginError, IBeeConfig, IToken } from "@beefree.io/sdk/dist/types/bee";
import { ExecCommands } from "@beefree.io/sdk/dist/types/bee";
import baselineNewsletterTemplateJson from "../baselineNewsletterTemplate.json";
import { logout } from "../services/authService";
import campaignService from "../services/campaignService";
import { NewsletterEdition, NewsletterEditionStatus, ReviewTodo } from "../types/newsletterEdition";
import { useSidebar } from "../contexts/SidebarContext";
import { OnboardingFormProps } from "./Onboarding/Types";
import TemplateEditorSaveModal from "./TemplateEditorSaveModal";
import SidePanel from "./SidePanel";
import * as cheerio from 'cheerio';
import OnboardingPopup, { onboardingSteps } from "./Onboarding/steps/OnboardingPopup";
import { selectUserId, selectUserEmail, selectOrganization, selectIsOperatorSession } from "../store/slices/authUserSlice";
import { useSelector } from "react-redux";
import GiveFeedbackForm from "./GiveFeedbackForm";
import SendTestEmailModal from "./SendTestEmailModal";
import NewsletterStatusPill from "./NewsletterStatusPill";
import IngredientPanel, { IngredientData } from "./IngredientPanel";
import ToolsPanel from "./ToolsPanel";
import NewsletterApprovalConfirmModal from "./NewsletterApprovalConfirmModal";
import ReviewPipelinePanel from "./ReviewPipelinePanel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";


export interface TemplateEditorProps {
  onboardingProps?: OnboardingFormProps;
}

/**
 * The BeeFree plugin needs a token to display.
 */
const fetchBeeFreeToken = async (): Promise<IToken> => {
  const token = await campaignService.getBeeFreeToken();
  return {
    access_token: token,
    token_type: "bearer",
    expires_in: 300,
  } as unknown as IToken;
};

const fetchNewsletterEdition = async (
  newsletterEditionId: string,
): Promise<NewsletterEdition> => {
  return campaignService.getNewsletterEdition(newsletterEditionId);
};

/**
 * Download the current template as an HTML file no additions
 */
const downloadHtml = (html: string, filename: string) => {
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Download the current template as an HTML file that is compatible with Convertkit
 * https://help.kit.com/en/articles/2810363-creating-a-custom-html-email-template
 *
 * ConvertKit requires a "{{ unsubscribe_url }}" to be present in the template
 * Optionally a "{{ subscriber_preferences_url }}" can be present in the template
 *
 * We search the template and find the last "Unsubscribe" token in the HTML string and add an <a> tag
 * around it to inject the unsubscribe url
 *
 * If a "Subscription Preferences" token is found, we inject the subscriber preferences url
 *
 * TODO: let's create a "Custom Row" for this with an unsubscribe button but this requires the BeeFree Core plan: https://docs.beefree.io/beefree-sdk/rows/custom-rows/understanding-custom-rows
 */
const downloadConvertkitHtml = async (html: string, filename: string) => {
  const UNSUBSCRIBE_TOKEN = "unsubscribe";
  const SUBSCRIPTION_PREFERENCES_TOKEN = "subscription preferences";

  // find the last "unsubscribe" token and get its index
  const $ = cheerio.load(html);
  const lastUnsubscribeIndex = $("body").html()!.toLowerCase().lastIndexOf(UNSUBSCRIBE_TOKEN);

  // we MUST have an unsubscribe button or <p> to add the unsubscribe url to, if we want to support ConvertKit
  if (lastUnsubscribeIndex === -1) {
    alert("Please add an unsubscribe link to the newsletter edition if you want to send your newsletter with ConvertKit.");
    return;
  }

  $("body").html(
    $("body").html()!.slice(0, lastUnsubscribeIndex) +
    `<a href="{{ unsubscribe_url }}">${$("body").html()!.slice(lastUnsubscribeIndex, lastUnsubscribeIndex + UNSUBSCRIBE_TOKEN.length)}</a>` +
    $("body").html()!.slice(lastUnsubscribeIndex + UNSUBSCRIBE_TOKEN.length)
  );

  // we'll do the same thing for the subscription preferences token
  const lastSubscriptionPreferencesIndex = $("body").html()!.toLowerCase().lastIndexOf(SUBSCRIPTION_PREFERENCES_TOKEN);
  if (lastSubscriptionPreferencesIndex !== -1) {
    $("body").html(
      $("body").html()!.slice(0, lastSubscriptionPreferencesIndex) +
      `<a href="{{ subscriber_preferences_url }}">${$("body").html()!.slice(lastSubscriptionPreferencesIndex, lastSubscriptionPreferencesIndex + SUBSCRIPTION_PREFERENCES_TOKEN.length)}</a>` +
      $("body").html()!.slice(lastSubscriptionPreferencesIndex + SUBSCRIPTION_PREFERENCES_TOKEN.length)
    );
  }


  downloadHtml($.html(), filename);
};


/**
 * This is the component where we display the template editor.
 * The template editor is a licensed BeeFree editor.
 */
const TemplateEditor: React.FC<TemplateEditorProps> & { label: string } = () => {
  const { newsletterEditionId } = useParams<{ newsletterEditionId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasOnboardingParam = window.location.href.includes("onboarding");
  const [showOnboarding, setShowOnboarding] = useState(hasOnboardingParam);
  const [currentStep, setCurrentStep] = useState(0);

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isIngredientPanelOpen, setIsIngredientPanelOpen] = useState(false);
  const [isToolsPanelOpen, setIsToolsPanelOpen] = useState(false);
  const [isReviewPipelinePanelOpen, setIsReviewPipelinePanelOpen] = useState(false);
  // Review todos are their own entity on the server (not part of the edition),
  // so they get their own state: fetched on load for the badge, then kept in
  // sync by the panel (pipeline runs create them, checkbox clicks update them).
  const [reviewTodos, setReviewTodos] = useState<ReviewTodo[]>([]);

  // The review pipeline opens on the LEFT (so it doesn't cover BeeFree's
  // right-hand editing controls) and auto-collapses the app sidebar to make room.
  const { collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed } = useSidebar();
  const sidebarPrevCollapsedRef = useRef<boolean | null>(null);

  // use refs to persist values inside callbacks, avoiding stale closure issues
  const hasChangesRef = useRef(false);
  const intervalsWithoutChangesRef = useRef(0);

  // Handlers for navigation between steps
  const handleNextOnobardingStep = () => setCurrentStep(prev => Math.min(prev + 1, onboardingSteps.length - 1));
  const handlePreviousOnboardingStep = () => setCurrentStep(prev => Math.max(prev - 1, 0));
  const handleOnboardingClose = () => setShowOnboarding(false);

  const beeEditorRef = useRef<BeefreeSDK | null>(null);
  const latestTemplateRef = useRef<any>(null);
  const latestHtml = useRef<string>("");
  const lastIngredientContext = useRef<{
    ingredientIds: string;
    instructions?: string;
    ingredientFormat?: string;
  } | null>(null);
  const [isBeeEditorConfigured, setIsBeeEditorConfigured] = useState(false);
  const [isSavingToBackend, setIsSavingToBackend] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [hasFetchedDefaultTemplate, setHasAttemptedDefaultTemplate] = useState(false);
  const [editorRefreshKey, setEditorRefreshKey] = useState(0);
  const [isIngredientCreationInProgress, setIsIngredientCreationInProgress] = useState(false);
  const [templateUpdateCounter, setTemplateUpdateCounter] = useState(0);

  // the modal that pops up when we click "Save as template"
  const [isSaveAsTemplateModalOpen, setIsSaveAsTemplateModalOpen] = useState(false);
  // the modal that pops up when we click "Send test"
  const [isSendTestEmailModalOpen, setIsSendTestEmailModalOpen] = useState(false);
  // the modal that pops up when confirming newsletter approval
  const [isApprovalConfirmModalOpen, setIsApprovalConfirmModalOpen] = useState(false);
  const [approvalSiblingEditions, setApprovalSiblingEditions] = useState<NewsletterEdition[] | null>(null);
  // we need this to know if we should let the user export the template or not
  // because the BeeFree plugin calls the onSave callback asynchronously, and if we click "download as HTML" before it's done, it'll download the older version of the template
  const [templateIsSaved, setTemplateIsSaved] = useState(true);

  // to show a confirmation that saving was successful
  const [saveTemplateSuccess, setSaveTemplateSuccess] = useState(false);
  const handleSaveTemplateSuccess = (success: boolean) => {
    if (success) {
      setSaveTemplateSuccess(true);

      // clear success banner so it's not sticky
      setTimeout(() => {
        setSaveTemplateSuccess(false);
      }, 2000);
    } else {
      setSaveTemplateSuccess(false);
    }
  };

  const [submitFeedbackSuccess, setSubmitFeedbackSuccess] = useState(false);
  const handleSubmitSuccess = (success: boolean) => {
    if (success) {
      setSubmitFeedbackSuccess(true);

      // clear success banner so it's not sticky
      setTimeout(() => {
        setSubmitFeedbackSuccess(false);
      }, 2000);
    } else {
      setSubmitFeedbackSuccess(false);
    }
  };

  const [sendTestEmailSuccess, setSendTestEmailSuccess] = useState(false);
  const handleSendTestEmailSuccess = (success: boolean) => {
    if (success) {
      setSendTestEmailSuccess(true);

      // clear success banner so it's not sticky
      setTimeout(() => {
        setSendTestEmailSuccess(false);
      }, 2000);
    } else {
      setSendTestEmailSuccess(false);
    }
  };

  const userEmail = useSelector(selectUserEmail);
  const userId = useSelector(selectUserId);
  const organization = useSelector(selectOrganization);
  const canApproveFromPreparing = !!organization?.feature_flags?.approve_from_preparing;
  // Operator sessions (admin "login as") see the internal tooling — same flag
  // the Sidebar gates Content Tools on.
  const isOperator = useSelector(selectIsOperatorSession);

  const beeFreeHtmlContainerId = "template-editor-container";
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  const fourDaysFromToday = new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString();
  const [newsletterEdition, setNewsletterEdition] = useState<NewsletterEdition>(
    {
      id: "",
      title: `Edit Your Sample Newsletter`,
      email_template: baselineNewsletterTemplateJson,
      html_content: "",
      newsletter_timestamp: tomorrow,
      scheduled_send_start_date: fourDaysFromToday,
      created_at: "",
      updated_at: "",
      container: beeFreeHtmlContainerId,
      status: NewsletterEditionStatus.Preparing,
      newsletter_edition_links: [],
      newsletter_edition_change_requests: [],
    },
  );

  const [newsletterEditionTitle, setNewsletterEditionTitle] = useState(newsletterEdition.title);
  const [newsletterEditionPreheader, setNewsletterEditionPreheader] = useState(newsletterEdition.preheader || '');
  const newsletterEditionPreheaderRef = useRef<HTMLInputElement>(null);
  const newsletterEditionTitleRef = useRef<HTMLInputElement>(null);
  const scheduledSendStartDateRef = useRef<string>(newsletterEdition.scheduled_send_start_date);

  useEffect(() => {
    // on component mount, check for either approve or request change params
    const approve = searchParams.get("approve");
    const requestChanges = searchParams.get("request_changes");

    if (requestChanges === '1') {
      handleSidePanelOpen();
    }
    else if (approve === '1') {
      setIsApprovalConfirmModalOpen(true);
    }
    else {
      console.log("Unknown param detected");
    }
  }, []);

  useEffect(() => {
    // If we have a newsletter edition ID, fetch the existing edition
    if (newsletterEditionId && newsletterEditionId !== "new") {
      setHasAttemptedDefaultTemplate(true); // Existing editions don't need default template fetch

      fetchNewsletterEdition(newsletterEditionId).then(newsletterEdition => {
        setNewsletterEdition(newsletterEdition);
        setNewsletterEditionTitle(newsletterEdition.title);
        setNewsletterEditionPreheader(newsletterEdition.preheader || '');
        scheduledSendStartDateRef.current = newsletterEdition.scheduled_send_start_date;
      }).catch((error) => {
        console.error("Error fetching newsletter edition:", error);

        if (error.status === 401) {
          logout().finally(() => navigate("/login?status=timeout"));
        }
      });

      // Read-only fetch so the Review Pipeline badge reflects saved todos;
      // creating/updating them stays with the panel.
      campaignService.getReviewTodos(newsletterEditionId)
        .then(({ todos }) => setReviewTodos(todos))
        .catch(() => { /* badge just stays hidden */ });
    } else if (searchParams.get("blank") === "true") {
      // Blank post — skip default template, use baseline
      setHasAttemptedDefaultTemplate(true);
    } else {
      // If no newsletter edition ID (creating new), create with default if it exists
      campaignService.getDefaultTemplateOverride().then(defaultTemplate => {
        setNewsletterEdition(prevState => ({
          ...prevState,
          email_template: defaultTemplate.email_template,
          onboarding_blueprint_id: defaultTemplate.blueprint_id || undefined
        }));
        setHasAttemptedDefaultTemplate(true);
      }).catch((error) => {
        console.error("No default template override found, using baseline template:", error);
        // If there's no override (404) or other error, we'll use the baseline template
        setHasAttemptedDefaultTemplate(true);
      });
    }
  }, [newsletterEditionId]);

  const showErrorMessage = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 3000);
  };

  const saveToBackend = async (email_template?: object, html_content?: string) => {
    setIsSavingToBackend(true);
    await saveToBackend_(email_template, html_content)
      .catch(error => {
        console.error("Error saving newsletter edition to backend", error);
        showErrorMessage("Error saving newsletter edition.");
      })
      .finally(() => setIsSavingToBackend(false));
  };

  const saveToBackend_ = async (email_template?: object, html_content?: string) => {
    const newTitle = newsletterEditionTitleRef.current!.value;
    if (!newsletterEditionId) {
      campaignService
        .createNewsletterEdition({
          title: newTitle,
          email_template: email_template ?? newsletterEdition.email_template,
          html_content: html_content ?? newsletterEdition.html_content,
          newsletter_timestamp: newsletterEdition.newsletter_timestamp,
          scheduled_send_start_date: scheduledSendStartDateRef.current,
        })
        .then(newNewsletterEditionId => {
          navigate(`/newsletterEdition/${newNewsletterEditionId}`);
        })
        .then(_ => setIsBeeEditorConfigured(false))
        .catch(error => {
          console.error("Error creating newsletter edition:", error);

          if (error.status === 401) {
            logout().finally(() => navigate("/login?status=timeout"));
          }
        });
    } else {
      campaignService
        .updateNewsletterEdition(newsletterEditionId!, {
          title: newTitle.trim(),
          email_template: email_template!,
          html_content: html_content,
          newsletter_timestamp: newsletterEdition.newsletter_timestamp,
          scheduled_send_start_date: scheduledSendStartDateRef.current,
          preheader: newsletterEditionPreheaderRef.current?.value ?? '',
        })
        .then((updated) => {
          // Todos live in their own table and state — the edition returned by
          // a save/autosave can't carry or clobber them.
          setNewsletterEdition(updated);
          // Trigger ingredient list refresh when template is updated
          setTemplateUpdateCounter(prev => prev + 1);
        })
        .catch(error => {
          console.error("Error updating newsletter edition:", error);

          if (error.status === 401) {
            logout().finally(() => navigate("/login?status=timeout"));
          }
        });
    }
  };

  const handleUpdateNewsletterStatus = async (
    newsletterEditionId: string,
    status: NewsletterEditionStatus
  ): Promise<void> => {
    const toggledStatus = campaignService.getToggledStatus(status);
    if (!toggledStatus) return;

    if (toggledStatus === NewsletterEditionStatus.Approved) {
      setIsApprovalConfirmModalOpen(true);
      return;
    }

    try {
      const updated = await campaignService.updateNewsletterEditionStatus(newsletterEditionId, toggledStatus);
      setNewsletterEdition(updated);
    } catch (error: any) {
      console.error("Error updating newsletter edition:", error);

      if (error.status === 401) {
        logout().finally(() => navigate("/login?status=timeout"));
      }
    }
  };

  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);

  const handleScheduledSendDateChange = async (date: Date | null): Promise<void> => {
    if (!date || !newsletterEditionId) return;

    // Format dates for confirmation message
    const currentDate = newsletterEdition.scheduled_send_start_date
      ? new Date(newsletterEdition.scheduled_send_start_date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      })
      : "not set";

    const newDate = date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to change the send date from ${currentDate} to ${newDate}?`
    );

    if (!confirmed) return;

    const isoDate = date.toISOString();
    setIsUpdatingSchedule(true);

    try {
      const updated = await campaignService.updateNewsletterEditionScheduledSendDate(newsletterEditionId, isoDate);
      setNewsletterEdition(updated);
      // Only update the ref after successful API call
      scheduledSendStartDateRef.current = isoDate;
    } catch (error: any) {
      console.error("Error updating scheduled send date:", error);

      if (error.status === 401) {
        logout().finally(() => navigate("/login?status=timeout"));
      }
    } finally {
      setIsUpdatingSchedule(false);
    }
  };

  const handleSave = (template: string, html: string) => {
    setTemplateIsSaved(false);
    latestHtml.current = html;
    const email_template = JSON.parse(template);

    setNewsletterEdition(prevState => ({ ...prevState, email_template }));
    saveToBackend(email_template, html).finally(() => {
      setTemplateIsSaved(true);
      handleSaveTemplateSuccess(true);
      // Trigger ingredient list refresh after save
      setTemplateUpdateCounter(prev => prev + 1);
    });
  };

  // handle clicking "Save as template", opening the modal
  const handleSaveAsTemplate = () => {
    // save the latest template, invoking handleSave callback
    setTemplateIsSaved(false);
    beeEditorRef.current!.save();
    // open the save as template options
    setIsSaveAsTemplateModalOpen(true);
  };

  // handle clicking "Send test", opening the modal
  const handleSendTest = () => {
    // save the latest template, invoking handleSave callback
    setTemplateIsSaved(false);
    beeEditorRef.current!.save();
    // open send test email form
    setIsSendTestEmailModalOpen(true);
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // tab just came into focus - reset inactivity counter
        intervalsWithoutChangesRef.current = 0;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // called every `autosave` seconds from the config
  const handleAutoSave = (template: string) => {
    // Skip autosave if ingredient creation is in progress to avoid overriding AI-generated content
    if (isIngredientCreationInProgress) {
      console.log("Skipping autosave - ingredient creation in progress");
      return;
    } else {
      console.log("Ingredient creation not in progress - autosaving.");
    }
    // only run inactivity logic if the tab isn't in focus and the user hasn't made changes
    if (document.hidden && !hasChangesRef.current) {
      // if the user is inactive for 4 autosave intervals, redirect to dashboard
      if (intervalsWithoutChangesRef.current === 4) {
        navigate(`/dashboard`);
      } else {
        intervalsWithoutChangesRef.current += 1;
      }
    }

    // reset change flag after autosave
    hasChangesRef.current = false;

    // save the latest template to the backend
    // doesn't call .save() to persist it in the editor's local storage
    setTemplateIsSaved(false);
    const email_template = JSON.parse(template);
    setNewsletterEdition(prevState => ({ ...prevState, email_template }));
    saveToBackend(email_template).finally(() => {
      setTemplateIsSaved(true);
      handleSaveTemplateSuccess(true);
      // Trigger ingredient list refresh after autosave
      setTemplateUpdateCounter(prev => prev + 1);
    });
  }

  const handleChange = (template: string, details: any) => {
    hasChangesRef.current = true;
    intervalsWithoutChangesRef.current = 0;
    try { latestTemplateRef.current = JSON.parse(template); }
    catch (error) {
      console.error("Error parsing template in handleChange:", error);
    }
  }

  const beeFreeConfig: IBeeConfig = {
    uid: userId,
    container: beeFreeHtmlContainerId,
    language: "en-US",
    autosave: 30,
    trackChanges: true,
    onSave: handleSave,
    onSaveAsTemplate: handleSaveAsTemplate,
    onSend: handleSendTest,
    onAutoSave: handleAutoSave,
    onChange: handleChange,
    onError: (error: BeePluginError) => {
      campaignService.logBeeFreeError(
        'ERROR',
        error,
        newsletterEditionId,
        lastIngredientContext.current || undefined,
      );
    },
    onWarning(error: BeePluginError) {
      campaignService.logBeeFreeError(
        'WARNING',
        error,
        newsletterEditionId,
        lastIngredientContext.current || undefined,
      );
    },
    // onStart: () => {},
    // rowsConfiguration: {
    //   emptyRows: true,
    //   defaultRows: true,
    // },
  };

  useEffect(() => {
    if (newsletterEditionId && newsletterEdition.id === "") return;
    if (isBeeEditorConfigured) return;

    // For new newsletter editions, wait for default template attempt to complete
    if (!newsletterEditionId && !hasFetchedDefaultTemplate) {
      return;
    }

    console.info("Configuring BeeFree editor");

    fetchBeeFreeToken()
      .then(token => {
        beeEditorRef.current = new BeefreeSDK(token);
      })
      .then(async _ => {
        await beeEditorRef.current?.start(beeFreeConfig, newsletterEdition.email_template);
      })
      .then(_ => console.info("BeeFree editor configured"))
      .then(_ => setIsBeeEditorConfigured(true))
      .catch(error => console.info("Error configuring BeeFree editor", error));
  }, [newsletterEditionId, newsletterEdition.email_template, hasFetchedDefaultTemplate]);

  // Reinitialize BeeFree editor when ingredient creation begins
  useEffect(() => {
    if (isIngredientCreationInProgress && isBeeEditorConfigured) {
      console.info("Reconfiguring BeeFree editor for ingredient creation");
      setIsBeeEditorConfigured(false);
    }
  }, [isIngredientCreationInProgress, isBeeEditorConfigured]);

  // Separate useEffect for forced refreshes (when adding ingredients)
  useEffect(() => {
    if (editorRefreshKey === 0) return; // Don't refresh on initial load

    console.info("Force refreshing BeeFree editor due to ingredient addition");
    setIsBeeEditorConfigured(false); // This will trigger the main useEffect to reload
  }, [editorRefreshKey]);

  let successBannerText = '';
  if (submitFeedbackSuccess) {
    successBannerText = "Thanks for your feedback!";
  } else if (sendTestEmailSuccess) {
    successBannerText = "Successfully sent test email!"
  }

  const handleSidePanelOpen = () => {
    const newParams = new URLSearchParams();
    newParams.set("request_changes", "1");
    navigate({
      pathname: location.pathname,
      search: newParams.toString(),
    }, { replace: true });

    setIsSidePanelOpen(true);
  };

  const handleSidePanelClose = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete("request_changes");
    navigate({
      pathname: location.pathname,
      search: newParams.toString(),
    }, { replace: true });

    setIsSidePanelOpen(false);
  };

  const handleIngredientPanelOpen = () => {
    setIsIngredientPanelOpen(true);
  };

  const handleIngredientPanelClose = () => {
    setIsIngredientPanelOpen(false);
  };

  const handleToolsPanelOpen = () => {
    setIsToolsPanelOpen(true);
  };

  const handleToolsPanelClose = () => {
    setIsToolsPanelOpen(false);
  };

  const handleTemplateUpdated = async () => {
    try {
      const updatedNewsletterEdition = await fetchNewsletterEdition(newsletterEditionId!);
      // Set isBeeEditorConfigured to false first so that when the new template
      // lands, the main useEffect will re-initialize the editor with it.
      setIsBeeEditorConfigured(false);
      setNewsletterEdition(updatedNewsletterEdition);
    } catch (error) {
      console.error('Error refreshing after template update:', error);
    }
  };

  // Lighter refresh for small server-side template edits (e.g. the review
  // pipeline's alt-text auto-fix): swap the template into the RUNNING editor
  // via bee.reload() instead of tearing down and re-initializing the whole
  // editor (token fetch + start()), which flashes and loses editor state.
  const handleTemplateSoftRefresh = async () => {
    try {
      const updatedNewsletterEdition = await fetchNewsletterEdition(newsletterEditionId!);
      setNewsletterEdition(updatedNewsletterEdition);
      if (beeEditorRef.current && updatedNewsletterEdition.email_template) {
        beeEditorRef.current.reload(updatedNewsletterEdition.email_template as any);
      }
    } catch (error) {
      console.error('Error refreshing template after auto-fix:', error);
    }
  };

  const removeApprovalParamFromUrl = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("approve");
    navigate({
      pathname: location.pathname,
      search: newParams.toString(),
    }, { replace: true });
  };

  // When the approval modal opens (via the Approve button or the email magic
  // link's ?approve=1), load the org's other editions so the modal can warn
  // when another approved edition is scheduled to send within 7 days of this
  // one. Re-runs once the edition finishes loading (id changes from "" to the
  // real id).
  useEffect(() => {
    if (!isApprovalConfirmModalOpen || !newsletterEdition.id) {
      setApprovalSiblingEditions(null);
      return;
    }
    campaignService.listNewsletterEditions()
      .then(setApprovalSiblingEditions)
      .catch(error => {
        // Don't block approval if the sibling-edition lookup fails
        console.error("Error checking for closely scheduled editions:", error);
        setApprovalSiblingEditions(null);
      });
  }, [isApprovalConfirmModalOpen, newsletterEdition.id]);

  const handleApprovalConfirm = async (selectedSendDate?: string) => {
    try {
      if (selectedSendDate) {
        await campaignService.updateNewsletterEditionScheduledSendDate(
          newsletterEditionId!, selectedSendDate);
        scheduledSendStartDateRef.current = selectedSendDate;
      }

      await campaignService.updateNewsletterEditionStatus(
        newsletterEditionId!, NewsletterEditionStatus.Approved);

      setNewsletterEdition(prev => ({
        ...prev,
        status: NewsletterEditionStatus.Approved,
        ...(selectedSendDate ? { scheduled_send_start_date: selectedSendDate } : {}),
      }));
      setIsApprovalConfirmModalOpen(false);
      removeApprovalParamFromUrl();
    } catch (error) {
      console.error("Error approving newsletter:", error);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalConfirmModalOpen(false);
    removeApprovalParamFromUrl();
  };
  const handleScrollToIngredientRow = useCallback((rowUuid: string) => {
    if (beeEditorRef.current) {
      // Use latest template from ref (reflects drag reorders) with state as fallback
      const template = latestTemplateRef.current || newsletterEdition.email_template;
      const rows = (template as any)?.page?.rows || [];
      const rowIndex = rows.findIndex((row: any) => row.customFields?.row_uuid === rowUuid);
      if (rowIndex >= 0) {
        beeEditorRef.current.execCommand(ExecCommands.SCROLL, { target: { row: rowIndex + 1 } });
        beeEditorRef.current.execCommand(ExecCommands.HIGHLIGHT, { target: { row: rowIndex + 1 } });
      }
    }
  }, [newsletterEdition.email_template]);

  const handleScrollToReviewRow = useCallback((todo: ReviewTodo) => {
    if (!beeEditorRef.current) return;
    const template = latestTemplateRef.current || newsletterEdition.email_template;
    const rows = (template as any)?.page?.rows || [];
    // row_uuid is rarely present on rows, so fall back to the stored row index.
    let rowIndex = -1;
    if (todo.row_uuid) {
      rowIndex = rows.findIndex((row: any) => row.customFields?.row_uuid === todo.row_uuid);
    }
    if (rowIndex < 0 && typeof todo.row_index === "number") {
      rowIndex = todo.row_index;
    }
    if (rowIndex >= 0 && rowIndex < rows.length) {
      beeEditorRef.current.execCommand(ExecCommands.SCROLL, { target: { row: rowIndex + 1 } });
      beeEditorRef.current.execCommand(ExecCommands.HIGHLIGHT, { target: { row: rowIndex + 1 } });
    }
    // If the row can't be resolved, the section no longer exists; silently skip.
  }, [newsletterEdition.email_template]);

  const openReviewPipeline = () => {
    sidebarPrevCollapsedRef.current = sidebarCollapsed;
    setSidebarCollapsed(true);
    setIsReviewPipelinePanelOpen(true);
  };

  const closeReviewPipeline = () => {
    setIsReviewPipelinePanelOpen(false);
    if (sidebarPrevCollapsedRef.current !== null) {
      setSidebarCollapsed(sidebarPrevCollapsedRef.current);
      sidebarPrevCollapsedRef.current = null;
    }
  };

  //TODO (seif): check the naming if it is org_ingredientData or IngredientData
  const handleAddIngredient = async (ingredientData: IngredientData): Promise<void> => {
    try {
      lastIngredientContext.current = {
        ingredientIds: ingredientData.ingredientIds,
        instructions: ingredientData.instructions,
        ingredientFormat: ingredientData.ingredientFormat
      };

      setIsIngredientCreationInProgress(true);

      await campaignService.addIngredientToNewsletterEdition(
        newsletterEditionId!,
        ingredientData.ingredientIds,
        ingredientData.instructions,
        ingredientData.ingredientFormat,
        ingredientData.model,
        ingredientData.provider,
        ingredientData.useSelectivePlaceholderMerge
      );

      console.log("Ingredient added successfully");

      // Refresh the newsletter edition to get the updated template
      const updatedNewsletterEdition = await fetchNewsletterEdition(newsletterEditionId!);
      setNewsletterEdition(updatedNewsletterEdition);

      // Force editor refresh by incrementing the refresh key
      setEditorRefreshKey(prev => prev + 1);
      // Also trigger ingredient list refresh
      setTemplateUpdateCounter(prev => prev + 1);

      // Close panel after adding
      setIsIngredientPanelOpen(false);
    } catch (error) {
      console.error("Error adding ingredient:", error);
      // Re-throw the error so IngredientPanel can handle it
      throw error;
    } finally {
      // Always clear the flag, even if there was an error
      console.log("Setting setIsIngredientCreationInProgress(false);")
      setIsIngredientCreationInProgress(false);
    }
  };

  return (
    <>
      {showOnboarding && (
        <OnboardingPopup
          onClose={handleOnboardingClose}
          currentStep={currentStep}
          onNext={handleNextOnobardingStep}
          onPrevious={handlePreviousOnboardingStep}
        />
      )}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          filter: isSaveAsTemplateModalOpen || isSendTestEmailModalOpen || isApprovalConfirmModalOpen ? "blur(4px)" : "none",
          pointerEvents: isSaveAsTemplateModalOpen || isSendTestEmailModalOpen || isApprovalConfirmModalOpen ? "none" : "auto",
        }}>
        <div
          className="inline-newsletter-metadata"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <div className="inline-metadata-item" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <input
              ref={newsletterEditionTitleRef}
              type="text"
              value={newsletterEditionTitle}
              onChange={(e) => setNewsletterEditionTitle(e.target.value)}
              style={{
                fontSize: "1.4em",
                fontWeight: "bold",
                border: "none",
                outline: "none",
                width: "100%",
                backgroundColor: "transparent",
                padding: "0",
                cursor: "text",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", padding: "0 0 4px 0" }}>
              <span style={{ fontSize: "0.85em", color: "#999", marginRight: "8px", whiteSpace: "nowrap" }}>
                Preheader:
              </span>
              <input
                ref={newsletterEditionPreheaderRef}
                type="text"
                value={newsletterEditionPreheader}
                onChange={(e) => setNewsletterEditionPreheader(e.target.value)}
                placeholder="Preview text shown after subject line in inbox..."
                style={{
                  fontSize: "0.95em",
                  color: "#555",
                  border: "none",
                  outline: "none",
                  width: "100%",
                  backgroundColor: "transparent",
                  padding: "2px 0",
                  cursor: "text",
                }}
              />
            </div>
          </div>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            <div style={{
              minWidth: "80px",
              height: "32px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              {saveTemplateSuccess &&
                <div style={{
                  backgroundColor: "#e0f7df",
                  color: "#2e7d32",
                  border: "1px solid #2e7d32",
                  borderRadius: "6px",
                  padding: "4px 10px",
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "0.9em",
                  height: "32px",
                }}>
                  ✅
                  <span style={{ marginLeft: "8px" }}>Saved</span>
                </div>
              }
            </div>

            <div className="inline-metadata-item"
              style={{
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <NewsletterStatusPill
                status={newsletterEdition.status}
                handleUpdate={() => handleUpdateNewsletterStatus(newsletterEdition.id, newsletterEdition.status)}
                canApproveFromPreparing={canApproveFromPreparing}
              />
            </div>

            <div className="inline-metadata-item">
              <DatePicker
                selected={newsletterEdition.scheduled_send_start_date ? new Date(newsletterEdition.scheduled_send_start_date) : null}
                onChange={handleScheduledSendDateChange}
                dateFormat="M/d/yyyy"
                popperPlacement="bottom"
                minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                customInput={
                  <button className="tertiary-info-button" style={{ cursor: "pointer", width: "180px", textAlign: "center" }}>
                    {isUpdatingSchedule
                      ? "Send Date: Updating..."
                      : newsletterEdition.scheduled_send_start_date
                        ? `Send Date: ${new Date(newsletterEdition.scheduled_send_start_date).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}`
                        : "Send Date: Select date"
                    }
                  </button>
                }
              />
            </div>

            {isOperator && (
              <button
                className="tertiary-info-button"
                style={{ cursor: "pointer" }}
                onClick={handleIngredientPanelOpen}
              >
                Ingredients (alpha)
              </button>
            )}

            <button
              className="tertiary-info-button"
              style={{ cursor: "pointer" }}
              onClick={openReviewPipeline}
            >
              AutoReview
              {reviewTodos.length > 0 && (
                <span style={{
                  marginLeft: "6px",
                  backgroundColor: reviewTodos.every(t => t.done) ? "#2e7d32" : "#E91E63",
                  color: "#fff",
                  borderRadius: "10px",
                  padding: "1px 7px",
                  fontSize: "0.75em",
                  fontWeight: 700,
                }}>
                  {reviewTodos.filter(t => !t.done).length > 0
                    ? `${reviewTodos.filter(t => !t.done).length} left`
                    : "✓"}
                </span>
              )}
            </button>

            <button
              style={{
                cursor: "pointer",
                backgroundColor: "#5d5ded",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "6px 14px",
                fontSize: "0.9em",
                fontWeight: 600,
                height: "32px",
              }}
              onClick={handleSidePanelOpen}
            >
              Feedback
            </button>
          </div>
          {errorMessage && (
            <div
              style={{
                position: "fixed",
                top: "10px",
                left: "10px",
                zIndex: 1000,
              }}>
              {errorMessage}
            </div>
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}>
            {/*  TODO: Add date picker  */}
            {/*<div className="inline-metadata-item">*/}
            {/*  <label style={{marginRight: "10px"}} htmlFor="date">*/}
            {/*    Prepared By Date*/}
            {/*  </label>*/}
            {/*  <input*/}
            {/*    type="date"*/}
            {/*    value={showNewsletterEditionString(newsletterEdition)}*/}
            {/*    onChange={dateOnchange(setNewsletterEdition)}*/}
            {/*    style={{marginBottom: "10px"}}*/}
            {/*  />*/}
            {/*</div>*/}
          </div>
        </div>

        {(submitFeedbackSuccess || sendTestEmailSuccess) &&
          <div
            style={{
              backgroundColor: "#e0f7df",
              color: "#2e7d32",
              border: "1px solid #2e7d32",
              borderRadius: "8px",
              padding: "8px 16px",
              marginBottom: "12px",
              maxWidth: "500px",
            }}>
            ✅
            <span style={{ marginLeft: "12px" }}>{successBannerText}</span>
          </div>
        }

        <div
          id={beeFreeHtmlContainerId}
          style={{
            width: "100%",
            // 60vh if the onboardingParam is true else 100vh
            height: window.location.href.includes("onboarding") ? "60vh" : "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            border: 0,
          }}
        />
      </div>
      {isSaveAsTemplateModalOpen && (
        <TemplateEditorSaveModal
          downloadAsHtml={() => {
            downloadHtml(latestHtml.current, `${newsletterEdition.title}.html`);
            setIsSaveAsTemplateModalOpen(false);
          }}
          downloadAsConvertkitHtml={() => {
            downloadConvertkitHtml(
              latestHtml.current,
              `${newsletterEdition.title} for Convertkit.html`,
            );
            setIsSaveAsTemplateModalOpen(false);
          }}
          onClose={() => setIsSaveAsTemplateModalOpen(false)}
          allowExport={templateIsSaved}
        />
      )}

      {isSendTestEmailModalOpen &&
        <SendTestEmailModal
          newsletterEditionId={newsletterEditionId}
          isLoading={!templateIsSaved}
          html={latestHtml.current}
          title={newsletterEdition.title}
          preheader={newsletterEditionPreheaderRef.current?.value ?? ''}
          onClose={() => setIsSendTestEmailModalOpen(false)}
          setSendTestEmailSuccess={handleSendTestEmailSuccess}
        />
      }

      {isApprovalConfirmModalOpen && (
        <NewsletterApprovalConfirmModal
          editionId={newsletterEdition.id}
          newsletterTitle={newsletterEdition.title}
          scheduledSendDate={newsletterEdition.scheduled_send_start_date}
          allEditions={approvalSiblingEditions}
          onConfirm={handleApprovalConfirm}
          onCancel={handleApprovalCancel}
        />
      )}

      <SidePanel isOpen={isSidePanelOpen} onClose={handleSidePanelClose}>
        <GiveFeedbackForm setSidePanelOpen={setIsSidePanelOpen} setSubmitSuccess={handleSubmitSuccess} />
      </SidePanel>

      <SidePanel isOpen={isIngredientPanelOpen} onClose={handleIngredientPanelClose}>
        <IngredientPanel onClose={handleIngredientPanelClose} onAddIngredient={handleAddIngredient} newsletterEditionId={newsletterEditionId} refreshKey={editorRefreshKey} onScrollToRow={handleScrollToIngredientRow} />
      </SidePanel>

      <SidePanel isOpen={isToolsPanelOpen} onClose={handleToolsPanelClose}>
        <ToolsPanel
          newsletterEdition={newsletterEdition}
          onClose={handleToolsPanelClose}
          onUpdateTitle={(newTitle: string) => setNewsletterEditionTitle(newTitle)}
          onTemplateUpdated={handleTemplateUpdated}
        />
      </SidePanel>

      <SidePanel isOpen={isReviewPipelinePanelOpen} onClose={closeReviewPipeline} side="left">
        {isReviewPipelinePanelOpen && newsletterEditionId && (
          <ReviewPipelinePanel
            newsletterEditionId={newsletterEditionId}
            scheduledSendStartDate={newsletterEdition.scheduled_send_start_date}
            onScrollToRow={handleScrollToReviewRow}
            onTodosUpdated={setReviewTodos}
            onTemplateChanged={handleTemplateSoftRefresh}
            onClose={closeReviewPipeline}
          />
        )}
      </SidePanel>

    </>
  );
};

TemplateEditor.label = "Edit Your Template";

export default TemplateEditor;
